import { MUITextField } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { useCallback, useEffect, useMemo } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { toast } from "react-toastify"
import { z } from "zod"
import { digitalManagerApi } from "../../../../../services/api"
import {
  DiMaDetailsContent,
  DiMaDetailsContentProps,
} from "../../../../components/DiMaDetailsContent"
import {
  AccessLevel,
  DimaFeatureShortNameEnum,
  hasUserAccessToFeature,
} from "../../../../models/Authorization"
import { DeviceCategoryGet, DeviceCategoryPut } from "../../../../models/DeviceCategory"
import { UserInfo } from "../../../../models/User"

const validationSchema = z.object({
  name: z.string().min(1, "Name is required"),
})

const returnURL = `/devices#categories`

export function CategoryDetails(props: {
  readonly deviceCategory: DeviceCategoryGet
  readonly setDeviceCategory: (updatedDeviceCategory: DeviceCategoryGet) => void
  readonly user: UserInfo
}) {
  const { deviceCategory, setDeviceCategory, user } = props

  const formMethods = useForm<DeviceCategoryPut>({
    resolver: zodResolver(validationSchema),
  })

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = formMethods

  useEffect(() => {
    reset({ name: deviceCategory.name })
  }, [deviceCategory, reset])

  const onSubmit = useCallback(
    (values: DeviceCategoryPut) =>
      digitalManagerApi
        .put<DeviceCategoryGet>(`/deviceCategories/${deviceCategory.id}`, values)
        .then((res) => {
          if (res.status === StatusCodes.OK) {
            toast.success("Device Category updated")
            setDeviceCategory(res.data)
          }
        })
        .catch(() => {
          toast.error("Cannot update device category")
          throw new Error("Cannot update device category")
        }),
    [deviceCategory.id, setDeviceCategory]
  )

  const submitForm = useCallback(() => {
    handleSubmit(onSubmit)()
  }, [handleSubmit, onSubmit])

  const canWrite = useMemo(
    () =>
      hasUserAccessToFeature(
        user.authorizations,
        DimaFeatureShortNameEnum.DM,
        AccessLevel.Write
      ),
    [user.authorizations]
  )

  const handleNameFieldChange = useCallback(
    (field: ControllerRenderProps<DeviceCategoryPut, "name">) => (value: string) =>
      field.onChange(value),
    []
  )

  const renderNameTextField = useCallback(
    ({ field }: { field: ControllerRenderProps<DeviceCategoryPut, "name"> }) => (
      <MUITextField
        id="name"
        type="text"
        size={32}
        name={field.name}
        value={field.value ?? ""}
        onChange={handleNameFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={!canWrite}
        errors={
          typeof errors.name?.message === "string"
            ? { name: { message: errors.name.message } }
            : undefined
        }
      />
    ),
    [canWrite, errors.name, handleNameFieldChange]
  )

  const detailsContentProps: DiMaDetailsContentProps<DeviceCategoryPut> = {
    formMethods,
    canWrite,
    inlineEdit: true,
    listItems: [
      {
        id: 0,
        label: "Name",
        displayItem: <div style={{ width: "100%" }}>{deviceCategory.name}</div>,
        editItem: (
          <Controller name="name" control={control} render={renderNameTextField} />
        ),
      },
    ],
  }

  return (
    <form onSubmit={submitForm}>
      <DiMaDetailsContent {...detailsContentProps} onSubmit={onSubmit} />
    </form>
  )
}
